/**
 * Date helpers for the Market Prices filters.
 *
 * - `todayLocal` returns YYYY-MM-DD in the editor's local timezone (so "today"
 *   matches what the calendar on their wall says, regardless of UTC offset).
 * - Presets are simple "last N days" windows. "All time" sends a far-past
 *   floor so the API's blank-range default (last 30 days) is bypassed.
 *
 * The DEFAULT_PRESET intentionally matches the API's blank-range default so
 * the UI shows the same data whether the user picks "Last 30 days" explicitly
 * or sends no dates at all.
 */

export function todayLocal(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function daysAgoLocal(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Sentinel "no lower bound" date — older than any realistic observation.
// Sent when the user picks "All time" so the API doesn't fall back to its
// blank-range 30-day default.
const ALL_TIME_FLOOR = '1900-01-01';

export interface DatePreset {
  key: string;
  label: string;
  /** Resolves to {dateFrom, dateTo}. dateTo is always today. */
  resolve: () => { dateFrom: string; dateTo: string };
}

export const DATE_PRESETS: ReadonlyArray<DatePreset> = [
  {
    key: '7d',
    label: 'Last 7 days',
    resolve: () => ({ dateFrom: daysAgoLocal(7), dateTo: todayLocal() }),
  },
  {
    key: '30d',
    label: 'Last 30 days',
    resolve: () => ({ dateFrom: daysAgoLocal(30), dateTo: todayLocal() }),
  },
  {
    key: '90d',
    label: 'Last 90 days',
    resolve: () => ({ dateFrom: daysAgoLocal(90), dateTo: todayLocal() }),
  },
  {
    key: '365d',
    label: 'Last year',
    resolve: () => ({ dateFrom: daysAgoLocal(365), dateTo: todayLocal() }),
  },
  {
    key: 'all',
    label: 'All time',
    resolve: () => ({ dateFrom: ALL_TIME_FLOOR, dateTo: todayLocal() }),
  },
] as const;

/** The preset that matches the API's blank-range default. Used as a hint
 *  in the UI ("blank inputs are equivalent to this preset"). Keep in sync
 *  with `DEFAULT_DATE_WINDOW_DAYS` in the API's market-prices.constants.ts. */
export const DEFAULT_PRESET_KEY = '7d';

/** Detect which preset (if any) the current date pair matches. Returns null
 *  for custom ranges or when both inputs are blank (the API default applies). */
export function detectActivePreset(
  dateFrom: string,
  dateTo: string,
): string | null {
  if (!dateFrom && !dateTo) return null;
  if (!dateFrom || !dateTo) return null;
  for (const preset of DATE_PRESETS) {
    const r = preset.resolve();
    if (r.dateFrom === dateFrom && r.dateTo === dateTo) return preset.key;
  }
  return null;
}

/** Format a YYYY-MM-DD string into "25 Mar 2026" for display. Returns "—"
 *  for empty / unparseable input. */
export function formatDayShort(iso: string): string {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}
